import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../context/LoadingContext';

// List of cooking-related terms for the loader text
const loaderTexts = [
  '<Cooking>',
  '<Basting>',
  '<Roasting>',
  '<Steaming>',
  '<Sizzling>',
  '<Grilling>',
  '<Braai\'ing>',
  '<Marinating>',
  '<Seasoning>',
  '<Preparing>'
];

interface ButtonInterceptorProps {
  children: React.ReactNode;
  to: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  triggerTransition?: () => boolean | Promise<boolean>;
  [key: string]: any; // For any other props
}

const ButtonInterceptor: React.FC<ButtonInterceptorProps> = ({
  children,
  to,
  className = '',
  onClick,
  triggerTransition,
  ...rest
}) => {
  const navigate = useNavigate();
  const { setIsLoading, setLoaderText } = useLoading();

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Call the original onClick handler if provided
    if (onClick) {
      onClick(e);
    }
    
    // If triggerTransition is provided, call it and only navigate if it returns true
    if (triggerTransition) {
      const shouldTransition = await Promise.resolve(triggerTransition());
      
      if (shouldTransition) {
        // Select a random loader text
        const randomIndex = Math.floor(Math.random() * loaderTexts.length);
        setLoaderText(loaderTexts[randomIndex]);
        
        // Show loader
        setIsLoading(true);
        
        // Navigate to the specified route with a slightly longer delay
        // to ensure the loader is fully visible before navigation
        setTimeout(() => {
          navigate(to);
        }, 200); // Increased delay to ensure the loader is shown before navigation
      }
    } else {
      // If no triggerTransition function is provided, behave as before
      // Select a random loader text
      const randomIndex = Math.floor(Math.random() * loaderTexts.length);
      setLoaderText(loaderTexts[randomIndex]);
      
      // Show loader immediately
      setIsLoading(true);
      
      // Navigate to the specified route with a slightly longer delay
      // to ensure the loader is fully visible before navigation
      setTimeout(() => {
        navigate(to);
      }, 200); // Increased delay to ensure the loader is shown before navigation
    }
  };

  return (
    <button 
      className={className} 
      onClick={handleClick} 
      {...rest}
    >
      {children}
    </button>
  );
};

export default ButtonInterceptor;